import React from "react";
import { Checkbox } from "antd";
import SectionHeader from "../../../common/section-header/index.js";
import TextInput from "../../../common/text-input/index.js";
import Text from "../../../common/typography/text.js";
import CommonButton from "../../../common/button/button.js";
import {
  ACCOUNT_CONTACTS_KEY,
  ACCOUNT_CONTACTS_SECTION,
  ACCOUNT_CONTACT_FIELDS,
  MAX_ACCOUNT_CONTACTS,
  createEmptyAccountContact,
} from "../accountEnrollmentFields.js";

// Formik marks the whole list touched (`true`) when the wizard tries to
// advance; otherwise touched is tracked per row and field.
const isContactFieldTouched = (touched, index, key) => {
  const list = touched?.[ACCOUNT_CONTACTS_KEY];
  if (list === true) return true;
  return Boolean(list?.[index]?.[key]);
};

const AccountContactsSection = ({
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
}) => {
  const contacts = values?.[ACCOUNT_CONTACTS_KEY] || [];
  const contactErrors = Array.isArray(errors?.[ACCOUNT_CONTACTS_KEY])
    ? errors[ACCOUNT_CONTACTS_KEY]
    : [];
  const canAdd = contacts.length < MAX_ACCOUNT_CONTACTS;

  const setContacts = (next) => setFieldValue(ACCOUNT_CONTACTS_KEY, next);

  const updateContact = (index, key, value) => {
    setContacts(
      contacts.map((contact, rowIndex) =>
        rowIndex === index ? { ...contact, [key]: value } : contact,
      ),
    );
    setFieldTouched(`${ACCOUNT_CONTACTS_KEY}[${index}].${key}`, true, false);
  };

  const errorFor = (index, key) =>
    isContactFieldTouched(touched, index, key)
      ? contactErrors[index]?.[key] || ""
      : "";

  return (
    <div className="account-enrollment-section" data-testid="account-contacts">
      <SectionHeader
        buttonLabel={canAdd ? "Add User" : undefined}
        onButtonClick={() => setContacts([...contacts, createEmptyAccountContact()])}
        title={ACCOUNT_CONTACTS_SECTION.title}
      />
      <Text
        className="account-enrollment-section-help"
        color="lightText"
        variant="tinyText"
      >
        {ACCOUNT_CONTACTS_SECTION.helperText}
      </Text>

      {contacts.length === 0 ? (
        <Text color="lightText" variant="smallText">
          No users added.
        </Text>
      ) : null}

      {contacts.map((contact, index) => (
        <div
          className="account-enrollment-contact"
          data-testid={`account-contact-${index}`}
          // Rows have no stable id; index keys are fine because rows are
          // only appended or removed, never reordered.
          key={index}
        >
          <div className="account-enrollment-contact-header">
            <Text variant="smallText">User {index + 1}</Text>
            <CommonButton
              onClick={() =>
                setContacts(contacts.filter((_, rowIndex) => rowIndex !== index))
              }
              variant="link"
            >
              Remove
            </CommonButton>
          </div>
          <div className="account-enrollment-field-grid">
            {ACCOUNT_CONTACT_FIELDS.map((field) => (
              <TextInput
                errorText={errorFor(index, field.key)}
                isRequired={Boolean(field.required)}
                key={field.key}
                onChange={(event) =>
                  updateContact(index, field.key, event.target.value)
                }
                placeholder={field.placeholder || `Enter ${field.label}`}
                setValue={(value) => updateContact(index, field.key, value)}
                titleText={field.label}
                value={contact[field.key] ?? ""}
              />
            ))}
            <div className="account-enrollment-contact-provider">
              <Checkbox
                checked={Boolean(contact.IsProvider)}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setContacts(
                    contacts.map((row, rowIndex) =>
                      rowIndex === index
                        ? { ...row, IsProvider: checked, ProviderNPI: checked ? row.ProviderNPI : "" }
                        : row,
                    ),
                  );
                }}
              >
                Provider
              </Checkbox>
            </div>
            {contact.IsProvider ? (
              <TextInput
                errorText={errorFor(index, "ProviderNPI")}
                isRequired
                onChange={(event) =>
                  updateContact(index, "ProviderNPI", event.target.value)
                }
                placeholder="Enter Provider NPI"
                setValue={(value) => updateContact(index, "ProviderNPI", value)}
                titleText="Provider NPI"
                value={contact.ProviderNPI ?? ""}
              />
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AccountContactsSection;
